/**
 * Mandatory handle picker for social signups, as a `(setup)` route (ADR 0006).
 *
 * New Google/Apple users are created with a placeholder username and
 * `username_pending: true`. This used to be `UsernameGate`, an absolute-fill
 * overlay rendered beside the root `Stack`; routing owns visibility now, so the
 * screen renders its content unconditionally and the group's guard decides
 * whether it is reachable at all.
 *
 * It carries NO `step` prop: the claim is auth-owned account setup, not one of
 * onboarding's six steps, so "step 3 of 6" means the same thing for the social
 * cohort (7 screens) and the email cohort (6).
 */

import { router } from 'expo-router';
import { useCallback, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';

import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import { claimUsername } from '@/features/auth/claim-username';
import { SetupLayout } from '@/features/setup/components/setup-layout';
import { SETUP_ROUTE_HREFS } from '@/features/setup/routes/setup-hrefs';
import { useAuth } from '@/hooks/use-auth';
import { useUsernameAvailability } from '@/hooks/use-username-availability';

export function UsernameScreen() {
  const { refreshSession } = useAuth();

  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { usernameAvailable, checkingUsername } = useUsernameAvailability(username);

  const canSubmit = usernameAvailable === true && !submitting;

  const handleChange = useCallback((text: string) => {
    // Mirror the register field: lowercase, letters + numbers only.
    setUsername(text.toLowerCase().replace(/[^a-z0-9]/g, ''));
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    const result = await claimUsername(username);
    if (result.ok) {
      // The auth service clears `username_pending`; refreshing the session is
      // what lets the group's guard stop forcing this route, and only then is
      // the move on to step 1 allowed through.
      await refreshSession();
      router.replace(SETUP_ROUTE_HREFS.welcome);
      return;
    }

    setError(result.message);
    setSubmitting(false);
  }, [canSubmit, username, refreshSession]);

  const status =
    usernameAvailable === true
      ? ('success' as const)
      : usernameAvailable === false
        ? ('error' as const)
        : undefined;

  const helperText = checkingUsername
    ? 'Checking availability...'
    : usernameAvailable === true
      ? 'Username is available!'
      : usernameAvailable === false
        ? 'Username is already taken'
        : '3-30 characters, letters and numbers only';

  return (
    <SetupLayout
      primaryLabel="Continue"
      onPrimary={handleSubmit}
      primaryDisabled={!canSubmit}
      primaryLoading={submitting}
    >
      <View className="flex-1 justify-center">
        {/* No width clamp here: SetupLayout owns the centred column. */}
        <View className="gap-6">
          <View className="gap-2">
            <Text className="text-3xl font-bold text-foreground">Pick your username</Text>
            <Text className="text-base text-muted-foreground">
              This is how friends will find you on Flavrly. You can change it later in settings.
            </Text>
          </View>

          <TextInput
            testID="username-screen-input"
            placeholder="Username"
            value={username}
            onChangeText={handleChange}
            autoCapitalize="none"
            autoCorrect={false}
            autoComplete="username"
            textContentType="username"
            returnKeyType="go"
            onSubmitEditing={handleSubmit}
            status={status}
            helperText={helperText}
            leftIcon={<Text className="text-muted-foreground text-base">@</Text>}
            rightIcon={
              checkingUsername ? (
                <ActivityIndicator size="small" />
              ) : usernameAvailable === true ? (
                <Text className="text-success text-lg">✓</Text>
              ) : usernameAvailable === false ? (
                <Text className="text-error text-lg">✕</Text>
              ) : undefined
            }
          />

          {error && (
            <Text className="text-sm text-destructive" accessibilityLiveRegion="polite">
              {error}
            </Text>
          )}
        </View>
      </View>
    </SetupLayout>
  );
}
